<script setup lang="ts">
/**
 * Opening band of /templates: the answer before the product.
 *
 * The page targets "resume templates", where the visitor's real question is
 * which format to use — so the format decision and a worked before/after entry
 * come first, and the gallery underneath is what they scroll into once they
 * know what they are choosing between. A page that puts the picker first and
 * the explanation behind a sign-up has nothing to rank with.
 */
const { t } = useI18n()

const formats = ['chronological', 'functional', 'hybrid'] as const

/** The example is stored as one string per column so the copy stays in i18n. */
const beforeLines = computed(() => t('templatesPage.intro.beforeBody').split('\n'))
const afterLines = computed(() => t('templatesPage.intro.afterBody').split('\n'))
</script>

<template>
  <section class="sofax bg-white dark:bg-gray-950">
    <div class="section-space">
      <div class="shell">
        <SofaxSectionHeading
          :eyebrow="t('templatesPage.intro.eyebrow')"
          :title="t('templatesPage.intro.title')"
          :text="t('templatesPage.intro.lead')"
          width="max-w-4xl"
        />

        <div class="grid grid-cols-1 gap-y-20 lg:gap-y-[6.25rem]">
          <!-- The two paragraphs that finish the answer, before anything is sold -->
          <div class="mx-auto max-w-3xl">
            <p class="text-lg">{{ t('templatesPage.intro.body1') }}</p>
            <p class="mt-6 text-lg">{{ t('templatesPage.intro.body2') }}</p>
          </div>

          <!-- Format decision: the question behind most "resume template" searches -->
          <div>
            <div class="mx-auto mb-10 max-w-3xl text-center lg:mb-14">
              <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('templatesPage.intro.formatTitle') }}</h2>
              <p class="mt-5">{{ t('templatesPage.intro.formatLead') }}</p>
            </div>

            <ul class="grid grid-cols-1 gap-6 md:grid-cols-3">
              <li
                v-for="format in formats"
                :key="format"
                class="flex h-full flex-col rounded-[0.625rem] bg-surface p-[1.875rem] dark:bg-gray-800"
              >
                <div class="display-heading display-heading-4 mb-3">
                  {{ t(`templatesPage.intro.formats.${format}.name`) }}
                </div>
                <p class="!mb-5 text-base">{{ t(`templatesPage.intro.formats.${format}.description`) }}</p>

                <dl class="mt-auto flex flex-col gap-3 border-t border-line pt-5 text-base dark:border-gray-700">
                  <div class="flex items-start gap-3">
                    <dt class="shrink-0 font-semibold text-ink dark:text-white">{{ t('templatesPage.intro.bestFor') }}</dt>
                    <dd>{{ t(`templatesPage.intro.formats.${format}.best`) }}</dd>
                  </div>
                  <div class="flex items-start gap-3">
                    <dt class="shrink-0 font-semibold text-ink dark:text-white">{{ t('templatesPage.intro.watchOut') }}</dt>
                    <dd>{{ t(`templatesPage.intro.formats.${format}.watch`) }}</dd>
                  </div>
                </dl>
              </li>
            </ul>

            <!--
              The deep dive on format is already an article. Linking it here
              keeps this section a summary rather than a second page competing
              with the post for the same query.
            -->
            <p class="mt-8 text-center">
              <LocaleLink to="/blog/the-best-resume-formats-in-2026-chronological-functional-or-hybrid" class="link">
                {{ t('templatesPage.intro.formatReadMore') }} →
              </LocaleLink>
            </p>
          </div>

          <!-- Worked example. "Use action verbs" is advice; this is the rewrite. -->
          <div>
            <div class="mx-auto mb-10 max-w-3xl text-center lg:mb-14">
              <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('templatesPage.intro.exampleTitle') }}</h2>
            </div>

            <div class="mx-auto grid max-w-5xl grid-cols-1 items-stretch gap-6 md:grid-cols-2">
              <div class="card">
                <div class="mb-4 flex items-center gap-3">
                  <i class="bi bi-x-circle shrink-0 text-xl text-muted" />
                  <div class="display-heading display-heading-5">{{ t('templatesPage.intro.before') }}</div>
                </div>
                <p
                  v-for="(line, index) in beforeLines"
                  :key="index"
                  class="text-base"
                  :class="index === 0 ? 'font-semibold text-ink dark:text-white' : ''"
                >
                  {{ line }}
                </p>
              </div>

              <div class="card border-accent bg-accent-soft/20 dark:bg-accent/10">
                <div class="mb-4 flex items-center gap-3">
                  <i class="bi bi-check-circle-fill shrink-0 text-xl text-accent" />
                  <div class="display-heading display-heading-5">{{ t('templatesPage.intro.after') }}</div>
                </div>
                <p
                  v-for="(line, index) in afterLines"
                  :key="index"
                  class="text-base"
                  :class="index === 0 ? 'font-semibold text-ink dark:text-white' : ''"
                >
                  {{ line }}
                </p>
              </div>
            </div>

            <p class="mx-auto mt-4 max-w-5xl text-sm text-muted dark:text-gray-400">
              {{ t('templatesPage.intro.exampleCaption') }}
            </p>

            <div class="mx-auto mt-10 max-w-3xl text-center">
              <p>{{ t('templatesPage.intro.exampleNote') }}</p>
              <div class="mt-8 flex flex-col items-center gap-3">
                <SofaxBtn to="/free-cv-builder" :label="t('templatesPage.intro.cta')" variant="accent" />
                <span class="text-sm text-muted dark:text-gray-400">{{ t('templatesPage.intro.ctaNote') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
